import {useState, useEffect, useRef} from "react";
import { Box, Typography, Button, FormControl, Accordion, AccordionSummary, AccordionDetails, InputLabel, Select, MenuItem, FormControlLabel } from "@mui/material";
import StyledSwitch from "@plannel/components/ui/StyledSwitch";

import ExpandMore from "@mui/icons-material/ExpandMore";
import SaveIcon from "@mui/icons-material/Save";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import "@plannel/scss/components/DataLoadValidation.scss"
import debug from "@plannel/utils/debug-util";
import { withTranslation } from "react-i18next";
import CodeUtil from "@plannel/pages/utils/code-util";
import { isEmpty } from "lodash";
import Snackbar from "@plannel/components/Snackbar";
import ModuleVersionSetting from "@plannel/components/ModuleVersionSetting";
import SegmentedControl from "@plannel/components/ui/SegmentedControl";
import MeasureNameSettings from "@plannel/components/MeasureNameSettings";

const ITEM_LEVEL = "itemLevel";
const RP_SETTINGS_CONFIG_CD = "RP_SETTINGS_CONFIG";
const MEASURE_DISPLAY_NAMES_CONFIG_CD = "MEASURE_DISPLAY_NAMES";

const RP_INV_DAYS_POLICY = "RP_INV_DAYS_POLICY";
const RP_INV_TURNOVER_POLICY = "RP_INV_TURNOVER_POLICY";
const CALC_TYPE = "CALC_TYPE";
const QTY = "QTY";
const AMT = "AMT";
const COGS = "COGS";
const RpSettings = ({ t }) => {
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [rpPlanConfig,setRpPlanConfig] = useState({});
  const [objVal, setObjVal] = useState(null);
  // 서버에서 config 를 새로 받은 순간에만 증가한다 — ModuleVersionSetting 의 재마운트 트리거.
  // objVal 자체를 key 로 쓰면 편집 한 글자마다 재마운트되어 입력 포커스가 날아간다.
  const [configLoadKey, setConfigLoadKey] = useState(0);
  const [itemLevelCds, setItemLevelCds] = useState([]);
  const [itemLevelCd, setItemLevelCd] = useState("");
  const [rpInvDaysPolicy, setRpInvDaysPolicy] = useState({ [CALC_TYPE]: AMT });
  const [rpInvTurnoverPolicy, setRpInvTurnoverPolicy] = useState({ [CALC_TYPE]: AMT });
  const [updateOrderMethod, setUpdateOrderMethod] = useState(false);
  // measure settings
  const [measureDisplayNames, setMeasureDisplayNames] = useState({});
  const measureDisplayNameConfig = useRef({ moduleCd: CodeUtil.module.RP, configCd: MEASURE_DISPLAY_NAMES_CONFIG_CD, activeFlg: true });

  const [rpMeasureList, setRpMeasureList] = useState([]);

  // QTY/AMT/COGS 계산방식 세그먼트 옵션
  const calcTypeOptions = [
    { value: QTY, label: t(QTY.toLowerCase()) },
    { value: AMT, label: t(AMT.toLowerCase()) },
    { value: COGS, label: t(COGS.toLowerCase()) },
  ];

  const RP_CONFIG_PARAMS = { moduleCd: "RP", configCd: RP_SETTINGS_CONFIG_CD };
  const setConfig = (objVal) => {
    setItemLevelCd(objVal?.ITEM_LEVEL || "");
    setUpdateOrderMethod(objVal?.UPDATE_ORDER_METHOD ?? false);
  }

  const retrieveRpConfig = () => {
    planConfigService.getConfigCd(RP_CONFIG_PARAMS)
      .then((response) => {
        const { data } = response;
        if (!isEmpty(data)) {
          setRpPlanConfig(response.data)
          const { objVal } = response.data;
          setObjVal(objVal);
          setConfig(objVal);

          setRpInvDaysPolicy(objVal?.[RP_INV_DAYS_POLICY] || { [CALC_TYPE]: AMT });
          setRpInvTurnoverPolicy(objVal?.[RP_INV_TURNOVER_POLICY] || { [CALC_TYPE]: AMT });
        } else {
          setRpPlanConfig({
            moduleCd: "RP",
            configCd: RP_SETTINGS_CONFIG_CD,
            objVal: {},
            activeFlg: true
          })
        }
      })
      .catch((error) => {
        debug(error);
      })
      .finally(() => {
        setConfigLoadKey(prev => prev + 1);
      })
  }

  const retrieveMeasureDisplayNames = () => {
    planConfigService.getMeasureDisplayNames(CodeUtil.module.RP)
      .then(response => {
        const { data } = response;
        if (!isEmpty(data)) {
          measureDisplayNameConfig.current = data;
        }
        setMeasureDisplayNames(data?.objVal ?? {});
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveHrchyConfig = () => {
    hrchyConfigService.getAll({ searchFilters: { hrchyNum: CodeUtil.hrchyNum.HRCHY } })
      .then(response => {
        const itemConfig = [];
        if (response.data?.results) {
          const result = response.data.results;
          result.sort((a, b) => {
            if (a.seqNum < b.seqNum) {
              return -1;
            }

            if (a.seqNum > b.seqNum) {
              return 1;
            }
            return 0;
          });
          for (const row of result) {
            if (row.itemHrchyFlg) {
              itemConfig.push(row);
            }
          }
          setItemLevelCds(itemConfig);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const handleSave = () => {
    planConfigService.upsert([{ ...rpPlanConfig, objVal }])
      .then((response) => {
        const { data } = response;
        if (response?.status === 200 && !isEmpty(data)) {
          const rpSettingConfigs = data[0];
          setRpPlanConfig(rpSettingConfigs)
          setObjVal(rpSettingConfigs?.objVal)
          setConfigLoadKey(prev => prev + 1);
          return true;
        }

        return false;
      }).catch((error) => {
      debug(error);
      return false;
    }).then((versionConfigSaved) => {
      handleSaveMeasureDisplayNames(versionConfigSaved);
    })
  }

  const handleSaveMeasureDisplayNames = (versionConfigSaved = true) => {
    planConfigService.upsert([{ ...measureDisplayNameConfig.current, objVal: measureDisplayNames }])
      .then(response => {
        const { data } = response;
        if (!isEmpty(data)) {
          measureDisplayNameConfig.current = data[0];
        }
        retrieveMeasureDisplayNames();
        if (versionConfigSaved) {
          setSnackInfo({ open: true, severity: "success", content: t("MSG_SaveSuccess") });
        } else {
          setSnackInfo({ open: true, severity: "error", content: t("MSG_SaveFailed") });
        }
      })
      .catch(e => {
        debug(e);
        setSnackInfo({ open: true, severity: "error", content: t("MSG_SaveFailed") });
      });
  };

  const onChangeRadioHandler = (e, targetPolicy) => {
    switch (targetPolicy) {
      case RP_INV_DAYS_POLICY:
        setRpInvDaysPolicy({ ...rpInvDaysPolicy, [e.target.name]: e.target.value });
        setObjVal({ ...objVal, [RP_INV_DAYS_POLICY]: { ...rpInvDaysPolicy, [e.target.name]: e.target.value } });
        break;
      case RP_INV_TURNOVER_POLICY:
        setRpInvTurnoverPolicy({ ...rpInvTurnoverPolicy, [e.target.name]: e.target.value });
        setObjVal({ ...objVal, [RP_INV_TURNOVER_POLICY]: { ...rpInvTurnoverPolicy, [e.target.name]: e.target.value } });
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    if (itemLevelCd) {
      setObjVal({...objVal, ITEM_LEVEL: itemLevelCd})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemLevelCd]);

  useEffect(() => {
    setObjVal({ ...objVal, UPDATE_ORDER_METHOD: updateOrderMethod })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateOrderMethod]);

  useEffect(() => {
    if (!isEmpty(itemLevelCds) && itemLevelCds.length >= 1) {
      setItemLevelCd(itemLevelCds[itemLevelCds.length-1]?.cd)
      retrieveRpConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemLevelCds]);

  useEffect(() => {
    retrieveHrchyConfig();
    retrieveMeasureDisplayNames();
    // rpMeasure 리스트 생성
    const measures = Object.values(CodeUtil.rpMeasure);
    setRpMeasureList(measures);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 1. 현재 저장된 itemLevelCd가 ID, CD 형태든 상관없이 객체 찾기
  const itemLevelCdStr = String(itemLevelCd ?? "");
  const currentMatchedItem = itemLevelCds?.find(
     item => String(item.id) === itemLevelCdStr || item.cd === itemLevelCdStr
   );

  // 2. 객체 없다면 첫 번째 아이템의 id를 기본값
  const selectValue = currentMatchedItem 
    ? String(currentMatchedItem.id) 
    : itemLevelCds 
      ? String(itemLevelCds[0]?.id ?? "") 
      : "";

  return (
    <Box id={"data-load-validation"} sx={{ mt: 1, height: "100%", width: "100%" }}>
      <Box sx={{ width: "100%", height: "calc(100% - 8px)", overflow: "auto" }}>
        {/* Version Setting */}
        <Box sx={{ width: "100%", height: "auto", mb: 2 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
              <Typography className="content-title">{t("version")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ModuleVersionSetting key={configLoadKey} moduleCd={"RP"} config={objVal} setConfig={setObjVal} />
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Turnover, Inv Days Setting */}
        <Box sx={{ width: "100%", height: "auto", mb: 2 }}>
          <Accordion defaultExpanded>
            {/* Accordion Title Area */}
            <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
              <Typography className="content-title">{t("invReport")}</Typography>
            </AccordionSummary>

            {/* Accordion Content Area */}
            <AccordionDetails>
              <Box className="content-main">

                {/* Inventory Days QTY 계산방식*/}
                <Box sx={{ width: "100%" }}>
                  <Box sx={{ alignContent: "center" }}>
                    <Typography variant="h6">{t("invDaysCalcFormula")}</Typography>
                  </Box>
                  <SegmentedControl
                    sx={{ mt: 0.5 }}
                    value={rpInvDaysPolicy?.[CALC_TYPE]}
                    onChange={(v) => onChangeRadioHandler({ target: { name: CALC_TYPE, value: v } }, RP_INV_DAYS_POLICY)}
                    options={calcTypeOptions}
                  />
                </Box>

                {/* Inventory Turnover AMT 계산방식  */}
                <Box sx={{ width: "100%" }}>
                  <Box sx={{ alignContent: "center", fontWeight: "bold" }}>
                    <Typography variant="h6">{t("invTurnoverCalcFormula")}</Typography>
                  </Box>
                  <SegmentedControl
                    sx={{ mt: 0.5 }}
                    value={rpInvTurnoverPolicy?.[CALC_TYPE]}
                    onChange={(v) => onChangeRadioHandler({ target: { name: CALC_TYPE, value: v } }, RP_INV_TURNOVER_POLICY)}
                    options={calcTypeOptions}
                  />
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Release */}
          <Box sx={{ width: "100%", height: "auto", mb: 2 }}>
              <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
                      <Typography className="content-title">{t("release")}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                      <Box className="content-main">
                        <Box sx={{ width: "100%" }}>
                          <Typography
                            variant="h6" sx={{ mb: 2 }}>{t("applyOverrideOrderMethod")}
                          </Typography>
                          <Box
                            sx={{ width: "80%", ml: 2 }}>
                            <FormControlLabel
                              control={
                                <StyledSwitch
                                  name="updateOrderMethod"
                                  checked={updateOrderMethod}
                                  onChange={(e) => setUpdateOrderMethod(e.target.checked)}
                                  sx={{ mr: 2 }}
                                />
                              }
                              label={t("useOverrideOrderMethod")}
                              key="UPDATE_ORDER_METHOD"
                            />
                          </Box>
                        </Box>
                      </Box>
                  </AccordionDetails>
              </Accordion>
          </Box>
        {/* Dashboard */}
        <Box sx={{ width: "100%", height: "auto", mb: 2 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />} aria-controls="panel1a-content" id="panel1a-header">
              <Typography className="content-title">{t("rpDashboard")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box className="content-main">
                <Box sx={{ width: "50%" }}>
                  <Typography
                    variant="h6" sx={{ mb: 2 }}>{t("itemLevel")}
                  </Typography>
                  <Box sx={{ width: "100%", minWidth: "800px", mt: 1.5, mb: 1 }}>
                    <FormControl sx={{ width: "30%" }}>
                      <InputLabel>{t(ITEM_LEVEL)}</InputLabel>
                      <Select
                        key={itemLevelCd}
                        variant={"outlined"}
                        id={ITEM_LEVEL}
                        name={ITEM_LEVEL}
                        size="small"
                        value={selectValue}
                        label={t(ITEM_LEVEL)}
                        onChange={event => {
                          setItemLevelCd(event.target.value);
                        }}>
                        {itemLevelCds?.map(itemLevel => {
                          return (
                            <MenuItem
                              key={String(itemLevel.id)}
                              value={String(itemLevel.id)}
                            >
                              {itemLevel.name}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Box>
                  <Typography
                    variant="h6" sx={{ ml: 1 }}>{t("MSG_RpSettingItemLevel")}
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
        {/* Measure Settings */}
        <Box sx={{ width: "100%", height: "auto", mb: 2 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore />} aria-controls="measure-panel-content" id="measure-panel-header">
              <Typography className="content-title">{t("measureSetting")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box className="content-main" sx={{ width: "100%" }}>
                <MeasureNameSettings
                  measures={rpMeasureList}
                  displayNames={measureDisplayNames}
                  onChange={setMeasureDisplayNames}
                  sx={{ mb: 1.5 }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
        <Button sx={{ width: "160px", height: "40px" }}
                className="content-button"
                color="primary"
                variant="contained"
                component="span"
                startIcon={<SaveIcon />}
                onClick={handleSave}>
          {t("save")}
        </Button>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </Box >

  );
};

export default withTranslation()(RpSettings);
